import { cn } from "@/shared/lib";

import type { DuelLine } from "../../model/duel";

type DuelBarsProps<Key extends string> = {
  lines: readonly DuelLine<Key>[];
  label: (key: Key) => string;
  format: (key: Key, value: number | null) => string;
  // Строка под мышью: подсвечивается и в квадрате сторон.
  hovered?: Key | null;
  onHover?: (key: Key | null) => void;
};

const barWidth = (value: number | null, top: number) =>
  value === null || top <= 0 ? "0%" : `${String(Math.round((value / top) * 100))}%`;

// Строки «ты — показатель — друг»: полосы от середины, у выигравшего — яркие и жирное число.
// В узком блоке (телефон) пять колонок не помещаются: название уходит строкой выше.
export const DuelBars = <Key extends string>({
  lines,
  label,
  format,
  hovered = null,
  onHover,
}: DuelBarsProps<Key>) => (
  <div className="@container flex flex-col gap-1">
    {lines.map((line) => {
      const top = Math.max(line.me ?? 0, line.them ?? 0);
      const meWins = line.winner === "me" || line.winner === "tie";
      const themWins = line.winner === "them" || line.winner === "tie";
      const dimmed = hovered !== null && hovered !== line.key;
      return (
        <div
          key={line.key}
          onMouseEnter={
            onHover === undefined
              ? undefined
              : () => {
                  onHover(line.key);
                }
          }
          onMouseLeave={
            onHover === undefined
              ? undefined
              : () => {
                  onHover(null);
                }
          }
          className={cn(
            "grid grid-cols-[48px_minmax(0,1fr)_minmax(0,1fr)_48px] items-center gap-x-2 gap-y-1 rounded-md px-1.5 py-1 transition-opacity @sm:grid-cols-[64px_minmax(0,1fr)_minmax(96px,auto)_minmax(0,1fr)_64px] @sm:gap-2.5",
            hovered === line.key && "bg-sidebar-active",
            dimmed && "opacity-45",
          )}
        >
          <span
            className={cn(
              "text-right font-mono",
              meWins ? "font-semibold text-foreground" : "text-subtle",
            )}
          >
            {format(line.key, line.me)}
          </span>
          <span
            aria-hidden="true"
            className="flex h-2.5 justify-end overflow-hidden rounded bg-sidebar-active"
          >
            <span
              className={cn("block h-full", meWins ? "bg-accent" : "bg-neutral")}
              style={{ width: barWidth(line.me, top) }}
            />
          </span>
          {/* В узком блоке название — строкой над полосками, в широком — между ними. */}
          <span className="order-first col-span-4 text-center text-[13px] text-muted @sm:order-none @sm:col-span-1">
            {label(line.key)}
          </span>
          <span
            aria-hidden="true"
            className="block h-2.5 overflow-hidden rounded bg-sidebar-active"
          >
            <span
              className={cn("block h-full", themWins ? "bg-rival" : "bg-neutral")}
              style={{ width: barWidth(line.them, top) }}
            />
          </span>
          <span
            className={cn("font-mono", themWins ? "font-semibold text-foreground" : "text-subtle")}
          >
            {format(line.key, line.them)}
          </span>
        </div>
      );
    })}
  </div>
);
